import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
  title: "", slug: "", excerpt: "", category: "India", read_time: "8 min read",
  image: "", author: "", author_avatar: "", date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }),
  content: "", tags: "", featured: false, published: true,
};

export default function AdminBlogEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  useEffect(() => {
    if (isNew) return;
    adminApi.get("/blog/admin/all").then((r) => {
      const p = r.data.find((x) => x.id === id);
      if (p) setForm({ ...p, tags: (p.tags || []).join(", ") });
      else toast.error("Post not found");
    }).catch(() => toast.error("Failed to load post")).finally(() => setLoading(false));
  }, [id, isNew]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      if (isNew) {
        const r = await adminApi.post("/blog", payload);
        toast.success("Post created");
        navigate(`/admin/blog/${r.data.id}`);
      } else {
        await adminApi.put(`/blog/${id}`, payload);
        toast.success("Post saved");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-foreground/50 text-center py-20">Loading…</div>;

  const inputCls = "w-full bg-transparent border border-border focus:border-accent transition-colors px-4 py-3 outline-none text-sm";
  const labelCls = "eyebrow block mb-2";

  return (
    <form onSubmit={save} className="space-y-8 max-w-5xl" data-testid="admin-blog-form">
      <div className="flex items-center justify-between">
        <Link to="/admin/blog" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-foreground/60 hover:text-accent">
          <ArrowLeft size={14} strokeWidth={1.25} /> Back to posts
        </Link>
        <button type="submit" disabled={saving} data-testid="admin-blog-save" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background hover:bg-accent hover:text-accent-foreground transition-colors uppercase text-[11px] tracking-[0.25em] disabled:opacity-50">
          <Save size={14} strokeWidth={1.5} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div>
        <div className="eyebrow mb-3">{isNew ? "New post" : "Edit post"}</div>
        <h1 className="font-serif text-3xl md:text-5xl">{isNew ? "Write a new journal entry." : form.title}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <label className={labelCls}>Title</label>
            <input data-testid="blog-title" required value={form.title} onChange={set("title")} className={inputCls} placeholder="A title that pulls people in" />
          </div>
          <div>
            <label className={labelCls}>Excerpt</label>
            <textarea required rows={3} value={form.excerpt} onChange={set("excerpt")} className={`${inputCls} resize-none`} placeholder="Two sentences that promise the article" />
          </div>
          <div>
            <label className={labelCls}>Content (HTML or plain text — paragraphs separated by &lt;p&gt; tags)</label>
            <textarea required rows={18} value={form.content} onChange={set("content")} className={`${inputCls} resize-y font-mono text-[13px] leading-relaxed`} placeholder="<p>Your story…</p>" />
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className={labelCls}>Slug (URL)</label>
            <input value={form.slug} onChange={set("slug")} className={inputCls} placeholder="auto from title if empty" />
          </div>
          <div>
            <label className={labelCls}>Category</label>
            <input required value={form.category} onChange={set("category")} className={inputCls} placeholder="India · Japan · Wellness" />
          </div>
          <div>
            <label className={labelCls}>Cover image URL</label>
            <input required value={form.image} onChange={set("image")} className={inputCls} placeholder="https://images.unsplash.com/…" />
            {form.image && <img src={form.image} alt="" className="mt-3 w-full aspect-[4/3] object-cover border border-border" />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Author</label>
              <input required value={form.author} onChange={set("author")} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Date</label>
              <input required value={form.date} onChange={set("date")} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Author avatar URL</label>
            <input value={form.author_avatar} onChange={set("author_avatar")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Read time</label>
            <input value={form.read_time} onChange={set("read_time")} className={inputCls} placeholder="8 min read" />
          </div>
          <div>
            <label className={labelCls}>Tags (comma separated)</label>
            <input value={form.tags} onChange={set("tags")} className={inputCls} placeholder="Honeymoon, Maldives" />
          </div>
          <div className="flex flex-col gap-3 pt-2">
            <label className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked={!!form.featured} onChange={set("featured")} className="accent-current" />
              <span>Featured (large at top of journal)</span>
            </label>
            <label className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked={!!form.published} onChange={set("published")} className="accent-current" />
              <span>Published (visible on site)</span>
            </label>
          </div>
        </div>
      </div>
    </form>
  );
}
